import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "./useWalletSafe";
import { toast } from "@/hooks/use-toast";

export interface NftLike {
  id: string;
  nft_id: string;
  user_id: string;
  wallet_address: string;
  created_at: string;
}

export interface NftInteractionData {
  likes_count: number;
  user_has_liked: boolean;
}

export function useNftInteractions(nftId: string) {
  const { connected, publicKey } = useWalletSafe();
  const [data, setData] = useState<NftInteractionData>({
    likes_count: 0,
    user_has_liked: false,
  });
  const [loading, setLoading] = useState(false);

  // Fetch interaction data
  const fetchData = useCallback(async () => {
    if (!nftId) return;

    try {
      // Get NFT aggregated data
      const { data: nftData } = await supabase
        .from("nfts")
        .select("likes_count")
        .eq("id", nftId)
        .maybeSingle();

      // Check if current user has liked
      let userHasLiked = false;
      if (connected && publicKey) {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.user?.id) {
          const { data: likeData } = await supabase
            .from("nft_likes")
            .select("id")
            .eq("nft_id", nftId)
            .eq("user_id", session.session.user.id)
            .maybeSingle();
          userHasLiked = !!likeData;
        }
      }

      setData({
        likes_count: nftData?.likes_count || 0,
        user_has_liked: userHasLiked,
      });
    } catch (error) {
      console.error("Error fetching NFT interactions:", error);
    }
  }, [nftId, connected, publicKey]);

  // Toggle like
  const toggleLike = useCallback(async () => {
    if (!connected || !publicKey) {
      toast({ title: "Connect your wallet first", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        toast({ title: "Please sign in first", variant: "destructive" });
        return;
      }

      const userId = session.session.user.id;
      const walletAddress = publicKey.toBase58();

      if (data.user_has_liked) {
        // Remove like
        await supabase
          .from("nft_likes")
          .delete()
          .eq("nft_id", nftId)
          .eq("user_id", userId);
        
        setData(prev => ({
          ...prev,
          user_has_liked: false,
          likes_count: Math.max(0, prev.likes_count - 1),
        }));
      } else {
        // Add like
        const { error } = await supabase.from("nft_likes").insert({
          nft_id: nftId,
          user_id: userId,
          wallet_address: walletAddress,
        });

        if (error) {
          if (error.code === "23505") {
            toast({ title: "You already liked this NFT" });
          } else {
            throw error;
          }
        } else {
          setData(prev => ({
            ...prev,
            user_has_liked: true,
            likes_count: prev.likes_count + 1,
          }));
          toast({ title: "❤️ Liked!" });
        }
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      toast({ title: "Failed to update like", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [connected, publicKey, nftId, data.user_has_liked]);

  // Log share
  const logShare = useCallback(async (platform: string) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      
      await supabase.from("share_logs").insert({
        nft_id: nftId,
        user_id: session?.session?.user?.id || null,
        wallet_address: publicKey?.toBase58() || null,
        platform: platform,
      });
    } catch (error) {
      console.error("Error logging share:", error);
    }
  }, [nftId, publicKey]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time subscription for likes
  useEffect(() => {
    const channel = supabase
      .channel(`nft-interactions-${nftId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "nft_likes", filter: `nft_id=eq.${nftId}` },
        () => fetchData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [nftId, fetchData]);

  return {
    ...data,
    loading,
    toggleLike,
    logShare,
    refresh: fetchData,
  };
}